/**
 * フォーム定義の型と入力検証（02 5.3.15 form・D-058〜061・D-115・D-116）。
 * - フォームコードは自動採番だけ（本文に form_code があれば定義外の項目として 400：D-019）
 * - FormDefinition（form_versions.definition_map の中身）は D-059。field_key は定義内で一意（FE-MSG-V-008）
 * - bot 判定の有効・しきい値の項目は 2026-09-29 のユーザー判断で廃止（D-058 は無効：インフラ実装メモ X-14）
 * - 登録と更新で同じ組み合わせの検査（formRuleIssues）を使う（画面と BE で同じ関数：3.6.3）
 */
import { z } from 'zod';
import { EMBED_TYPE_VALUES } from '../enums/embed-type';
import { FORM_STATUS_VALUES } from '../enums/form-status';
import { MESSAGES } from '../messages/messages';
import { CHOICE_FIELD_TYPES, FORM_FIELD_TYPE_VALUES } from './form-field-types';
import { zDate, zEmail, zNullable, zRequiredText, zShortText, zSystemCode, zUrl, zVersion } from './common.schema';
import { buildListQuerySchema, CommonAttributesSchema } from './list-query.schema';

export { CHOICE_FIELD_TYPES, FORM_FIELD_TYPE_LABELS, FORM_FIELD_TYPE_VALUES } from './form-field-types';
export type { FormFieldType } from './form-field-types';

/** 回答者の情報に写す field_key（D-060） */
export const SUBMITTER_FIELD_KEYS = {
  name: 'submitter_name',
  email: 'submitter_email',
  phone_number: 'submitter_phone_number',
  company_name: 'submitter_company_name',
} as const;

export const FORM_DEFAULTS = {
  button_text: '送信',
  button_color: '#10b981', // 262 r2：旧PCRM の既定（FormBuilder の submit_button_color）に戻した（257 の #EA580C から）
  completion_message: MESSAGES['MSG-S-054'].text,
  // 273：旧PCRMから落ちていた設定を戻す（自動返信・複数回答の許可・同意必須）。キーの無い保存済みの定義はこの値で読む
  auto_reply_enabled: false,
  is_multiple_submission_allowed: true,
  is_consent_required: false,
} as const;

/** 定義の JSON がこの大きさを超えたら S3 に置く（要件書 5.2・IaC 5.4.1） */
export const FORM_DEFINITION_S3_THRESHOLD_BYTES = 350 * 1024;
/** 定義の JSON の上限（超えたら 400 FORM_006） */
export const FORM_DEFINITION_MAX_BYTES = 1024 * 1024;
/** テスト送信用の一時キーの有効期間（D-116） */
export const FORM_TEST_KEY_TTL_SECONDS = 600;

const FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]{0,49}$/;
/** 送信を許可する連携先ドメイン（プロトコル・パスを含めない：MSG-V-027） */
export const ALLOWED_DOMAIN_PATTERN = /^(?!-)[a-z0-9-]{1,63}(\.[a-z0-9-]{1,63})+$/;

const zInteger = (min: number, max: number) =>
  z.number({ message: 'MSG-V-020' }).int('MSG-V-020').min(min, 'MSG-V-020').max(max, 'MSG-V-020');

export const FormFieldOptionSchema = z.object({ value: zRequiredText(100), label: zRequiredText(200) }).strict();

export const FormFieldValidationSchema = z
  .object({
    min_length: zInteger(0, 10000).optional(),
    max_length: zInteger(1, 10000).optional(),
    min_value: z.number({ message: 'MSG-V-020' }).int('MSG-V-020').optional(),
    max_value: z.number({ message: 'MSG-V-020' }).int('MSG-V-020').optional(),
  })
  .strict();

export const FormFieldSchema = z
  .object({
    field_key: z.string({ message: 'FE-MSG-V-009' }).regex(FIELD_KEY_PATTERN, 'FE-MSG-V-009'),
    label: zRequiredText(200),
    field_type: z.enum(FORM_FIELD_TYPE_VALUES, { message: 'FE-MSG-V-004' }),
    is_required: z.boolean({ message: 'MSG-V-020' }),
    placeholder: zShortText(200).optional(),
    help_text: zShortText(500).optional(),
    options: z.array(FormFieldOptionSchema).max(50, 'FE-MSG-V-010').optional(),
    validation: FormFieldValidationSchema.optional(),
    max_files: zInteger(1, 5).optional(),
  })
  .strict()
  .superRefine((field, context) => {
    if (CHOICE_FIELD_TYPES.has(field.field_type) && (!field.options || field.options.length === 0)) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['options'], message: 'FE-MSG-V-010' });
    }
    const limits = field.validation;
    if (limits?.min_length !== undefined && limits.max_length !== undefined && limits.min_length > limits.max_length) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['validation', 'max_length'], message: 'MSG-V-020' });
    }
    if (limits?.min_value !== undefined && limits.max_value !== undefined && limits.min_value > limits.max_value) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['validation', 'max_value'], message: 'MSG-V-020' });
    }
  });

export const FormDefinitionSchema = z
  .object({
    fields: z.array(FormFieldSchema, { message: 'MSG-V-033' }).min(1, 'MSG-V-033').max(100, 'MSG-V-020'),
    button_text: zShortText(50).optional(),
    button_color: z
      .string({ message: 'MSG-V-020' })
      .regex(/^#[0-9A-Fa-f]{6}$/, 'MSG-V-020')
      .optional(),
    completion_message: zShortText(2000).optional(),
    redirect_url: zNullable(zUrl).optional(),
    body: zShortText(5000).optional(),
    // 273：旧PCRMから落ちていた自動返信メールを戻す（有効なら件名・本文が必須）
    auto_reply_enabled: z.boolean({ message: 'MSG-V-020' }).optional(),
    auto_reply_subject: zShortText(200).optional(),
    auto_reply_body: zShortText(5000).optional(),
    // 273：旧PCRMから落ちていた複数回答の許可を戻す（false なら同じメールアドレスの 2 回目を受け付けない）
    is_multiple_submission_allowed: z.boolean({ message: 'MSG-V-020' }).optional(),
    // 273：旧PCRMから落ちていた個人情報の取り扱いへの同意必須を戻す
    is_consent_required: z.boolean({ message: 'MSG-V-020' }).optional(),
  })
  .strict()
  .superRefine((definition, context) => {
    if (definition.auto_reply_enabled) {
      if (!definition.auto_reply_subject) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['auto_reply_subject'], message: 'MSG-V-056' });
      }
      if (!definition.auto_reply_body) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['auto_reply_body'], message: 'MSG-V-091' });
      }
    }
    const seen = new Set<string>();
    definition.fields.forEach((field, index) => {
      if (seen.has(field.field_key)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['fields', index, 'field_key'],
          message: 'FE-MSG-V-008',
        });
      }
      seen.add(field.field_key);
    });
  });

export const NotificationTargetInputSchema = z
  .object({
    user_code: zSystemCode.optional(),
    email: zEmail.optional(),
  })
  .strict()
  .refine((target) => (target.user_code === undefined) !== (target.email === undefined), 'MSG-V-020');

const zAllowedDomain = z.preprocess(
  (value) => (typeof value === 'string' ? value.trim().toLowerCase() : value),
  z.string({ message: 'MSG-V-027' }).regex(ALLOWED_DOMAIN_PATTERN, 'MSG-V-027'),
);

export const FORM_INPUT_SHAPE = {
  form_name: zRequiredText(200, 'MSG-V-035'),
  form_title: zRequiredText(200, 'MSG-V-034'),
  description: zShortText(2000).optional(),
  embed_type: z.enum(EMBED_TYPE_VALUES, { message: 'FE-MSG-V-004' }),
  allowed_domains: z.array(zAllowedDomain).max(20, 'MSG-V-027').optional(),
  required_field_keys: z
    .array(z.string({ message: 'MSG-V-020' }).max(50, 'MSG-V-020'))
    .max(100)
    .optional(),
  definition: FormDefinitionSchema,
  maximum_response_count: zNullable(zInteger(1, 1000000)).optional(),
  close_date: zNullable(zDate).optional(),
  notification_targets: z.array(NotificationTargetInputSchema).max(20, 'MSG-V-020').optional(),
};

export interface FormRuleValues {
  embed_type?: string | undefined;
  required_field_keys?: readonly string[] | undefined;
  definition?: { fields: ReadonlyArray<{ field_key: string }> } | undefined;
}

/**
 * 組み合わせの検査（登録は本文、更新は今の値と本文を合わせた値で呼ぶ）。
 * - 送信時に必須にするキーは定義の field_key のどれか
 */
export function formRuleIssues(values: FormRuleValues): Array<{ path: string[]; message: string }> {
  const issues: Array<{ path: string[]; message: string }> = [];
  const keys = new Set((values.definition?.fields ?? []).map((field) => field.field_key));
  (values.required_field_keys ?? []).forEach((key, index) => {
    if (!keys.has(key)) {
      issues.push({ path: ['required_field_keys', String(index)], message: 'MSG-V-020' });
    }
  });
  return issues;
}

/** API-FORM-02 フォームの登録 */
export const FormCreateSchema = z
  .object(FORM_INPUT_SHAPE)
  .strict()
  .superRefine((values, context) => {
    for (const issue of formRuleIssues(values)) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: issue.path, message: issue.message });
    }
  });

/** API-FORM-04 フォームの更新（すべて任意＋version。組み合わせの検査は今の値と合わせて service で行う） */
export const FormUpdateSchema = z
  .object({
    ...FORM_INPUT_SHAPE,
    form_name: FORM_INPUT_SHAPE.form_name.optional(),
    form_title: FORM_INPUT_SHAPE.form_title.optional(),
    embed_type: FORM_INPUT_SHAPE.embed_type.optional(),
    definition: FORM_INPUT_SHAPE.definition.optional(),
    version: zVersion,
  })
  .strict();

/** API-FORM-06 公開・非公開・アーカイブの切替 */
export const FormStatusUpdateSchema = z
  .object({ form_status: z.enum(FORM_STATUS_VALUES, { message: 'MSG-V-020' }), version: zVersion })
  .strict();

/** API-FORM-07 API キーの再発行 */
export const FormApiKeyReissueSchema = z.object({ version: zVersion }).strict();

// ───────── 応答 ─────────

const DeletedMarksSchema = z.object({
  deleted_at: z.string().nullable().optional(),
  delete_batch_code: z.string().nullable().optional(),
});

export const NotificationTargetSchema = z.object({
  notification_target_number: z.number().int(),
  user_code: z.string().nullable(),
  user_name: z.string().nullable(),
  email: z.string().nullable(),
});

export const FormSchema = z
  .object({
    form_code: z.string(),
    form_name: z.string(),
    form_title: z.string(),
    description: z.string().nullable(),
    form_status: z.enum(FORM_STATUS_VALUES),
    embed_type: z.enum(EMBED_TYPE_VALUES),
    current_version_number: z.number().int(),
    allowed_domains: z.array(z.string()),
    required_field_keys: z.array(z.string()),
    view_count: z.number().int(),
    submission_count: z.number().int(),
    maximum_response_count: z.number().int().nullable(),
    close_date: z.string().nullable(),
    published_at: z.string().nullable(),
    created_by_name: z.string().nullable(),
  })
  .merge(CommonAttributesSchema)
  .merge(DeletedMarksSchema);

export const FormDefinitionOutputSchema = z.object({
  fields: z.array(
    z.object({
      field_key: z.string(),
      label: z.string(),
      field_type: z.enum(FORM_FIELD_TYPE_VALUES),
      is_required: z.boolean(),
      placeholder: z.string().nullable().optional(),
      help_text: z.string().nullable().optional(),
      options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
      validation: z
        .object({
          min_length: z.number().int().optional(),
          max_length: z.number().int().optional(),
          min_value: z.number().int().optional(),
          max_value: z.number().int().optional(),
        })
        .optional(),
      max_files: z.number().int().optional(),
    }),
  ),
  button_text: z.string(),
  button_color: z.string(),
  completion_message: z.string(),
  redirect_url: z.string().nullable(),
  body: z.string().nullable(),
  auto_reply_enabled: z.boolean(),
  auto_reply_subject: z.string().nullable(),
  auto_reply_body: z.string().nullable(),
  is_multiple_submission_allowed: z.boolean(),
  is_consent_required: z.boolean(),
});

export const FormDetailSchema = FormSchema.extend({
  definition: FormDefinitionOutputSchema,
  notification_targets: z.array(NotificationTargetSchema),
});

/** API-FORM-04 の応答（新しい版を作ったか：D-012 でキーを省略しない） */
export const FormUpdateResultSchema = FormDetailSchema.extend({ new_version_created: z.boolean() });

/** API-FORM-02 の応答（api_key の平文はこの応答と API-FORM-07 だけ：5.4） */
export const FormCreatedSchema = FormDetailSchema.extend({ api_key: z.string() });

export const FormApiKeySchema = z.object({ form_code: z.string(), api_key: z.string(), version: z.number().int() });

export const FormTestKeySchema = z.object({ test_key: z.string(), expires_at: z.string() });

export const EmbedCodesSchema = z.object({
  public_form_url: z.string(),
  embed: z.object({ html: z.string() }),
  widget: z.object({ html: z.string() }),
  api: z.object({
    endpoint_url: z.string(),
    method: z.literal('POST'),
    headers: z.record(z.string()),
    sample_body: z.string(),
    sample_curl: z.string(),
  }),
  api_key_placeholder: z.string(),
});

export const FORM_LIST_SORT_VALUES = ['created_at', 'form_name', 'form_code'] as const;

/** API-FORM-01（02 2.3.2） */
export const FormListQuerySchema = buildListQuerySchema(FORM_LIST_SORT_VALUES, '-created_at').extend({
  form_status: z.enum(FORM_STATUS_VALUES, { message: 'MSG-V-020' }).optional(),
  embed_type: z.enum(EMBED_TYPE_VALUES, { message: 'MSG-V-020' }).optional(),
});

export type FormField = z.infer<typeof FormFieldSchema>;
export type FormDefinitionInput = z.infer<typeof FormDefinitionSchema>;
export type FormDefinition = z.infer<typeof FormDefinitionOutputSchema>;
export type FormCreateInput = z.infer<typeof FormCreateSchema>;
export type FormUpdateInput = z.infer<typeof FormUpdateSchema>;
export type FormStatusUpdateInput = z.infer<typeof FormStatusUpdateSchema>;
export type NotificationTargetInput = z.infer<typeof NotificationTargetInputSchema>;
export type NotificationTarget = z.infer<typeof NotificationTargetSchema>;
export type Form = z.infer<typeof FormSchema>;
export type FormDetail = z.infer<typeof FormDetailSchema>;
export type FormUpdateResult = z.infer<typeof FormUpdateResultSchema>;
export type FormCreated = z.infer<typeof FormCreatedSchema>;
export type FormApiKey = z.infer<typeof FormApiKeySchema>;
export type FormTestKey = z.infer<typeof FormTestKeySchema>;
export type EmbedCodes = z.infer<typeof EmbedCodesSchema>;
export type FormListQuery = z.infer<typeof FormListQuerySchema>;

/** 定義に既定値を入れる（保存・応答の形。D-059） */
export function normalizeFormDefinition(definition: FormDefinitionInput): FormDefinition {
  return {
    fields: definition.fields.map((field) => ({
      field_key: field.field_key,
      label: field.label,
      field_type: field.field_type,
      is_required: field.is_required,
      placeholder: field.placeholder ?? null,
      help_text: field.help_text ?? null,
      ...(CHOICE_FIELD_TYPES.has(field.field_type) ? { options: field.options ?? [] } : {}),
      ...(field.validation && Object.keys(field.validation).length > 0 ? { validation: field.validation } : {}),
      ...(field.field_type === 'file' ? { max_files: field.max_files ?? 1 } : {}),
    })),
    button_text: definition.button_text ?? FORM_DEFAULTS.button_text,
    button_color: definition.button_color ?? FORM_DEFAULTS.button_color,
    completion_message: definition.completion_message ?? FORM_DEFAULTS.completion_message,
    redirect_url: definition.redirect_url ?? null,
    body: definition.body ?? null,
    auto_reply_enabled: definition.auto_reply_enabled ?? FORM_DEFAULTS.auto_reply_enabled,
    auto_reply_subject: definition.auto_reply_subject ?? null,
    auto_reply_body: definition.auto_reply_body ?? null,
    is_multiple_submission_allowed:
      definition.is_multiple_submission_allowed ?? FORM_DEFAULTS.is_multiple_submission_allowed,
    is_consent_required: definition.is_consent_required ?? FORM_DEFAULTS.is_consent_required,
  };
}

/**
 * 273：保存済みの定義（definition_map）に、後から足した設定の既定値を入れる。
 * 273 より前に保存した定義には auto_reply_*・is_multiple_submission_allowed・is_consent_required のキーが無い（移行はしない）。
 * 既定は 自動返信なし・複数回答を許可・同意不要（＝273 より前の動き）。キーがあればその値をそのまま使う
 */
export function withFormDefinitionDefaults(definition: FormDefinition): FormDefinition {
  const stored = definition as Partial<FormDefinition> & Pick<FormDefinition, 'fields'>;
  return {
    ...definition,
    auto_reply_enabled: stored.auto_reply_enabled ?? FORM_DEFAULTS.auto_reply_enabled,
    auto_reply_subject: stored.auto_reply_subject ?? null,
    auto_reply_body: stored.auto_reply_body ?? null,
    is_multiple_submission_allowed:
      stored.is_multiple_submission_allowed ?? FORM_DEFAULTS.is_multiple_submission_allowed,
    is_consent_required: stored.is_consent_required ?? FORM_DEFAULTS.is_consent_required,
  };
}
